const text = value => String(value ?? '').trim()
const ENTRY_ROLES = new Set(['ENTRY', 'TARGET_ENTRY', 'RUNNER_ENTRY'])
const account = row => text(row?.accountNumber ?? row?.account_number)
const ref = row => `${account(row)}:${text(row?.orderId ?? row?.order_id)}`
const historical = project => ['closed', 'reviewed'].includes(text(project?.stage).toLowerCase())
  || ['closed', 'reviewed'].includes(text(project?.setup_status).toLowerCase())
const trackedEntries = project => (Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : [])
  // Legacy entry references predate role tags. Still require a broker BUY leg.
  .filter(row => !row?.role || ENTRY_ROLES.has(text(row.role).toUpperCase()))
const validRef = row => account(row) && text(row?.orderId ?? row?.order_id)

export function setupEntryRefs(projects) {
  return [...new Set(projects.filter(project => !historical(project))
    .flatMap(trackedEntries).filter(validRef).map(ref))].sort()
}

/** Read-only projection. Holdings and notification contents are never fill evidence. */
export function reconcileSetupEntryStatus(project, orders) {
  if (historical(project)) return null
  const entries = [...new Map(trackedEntries(project).map(row => [ref(row), row])).values()]
  if (!entries.length) return null
  const symbol = text(project?.symbol).toUpperCase()
  const byRef = new Map()
  for (const order of orders || []) {
    const legs = order?.orderLegCollection
    if (!validRef(order) || order.promotedFromBracket || !Array.isArray(legs) || legs.length !== 1) continue
    if (!['BUY', 'BUY_TO_OPEN'].includes(legs[0]?.instruction)) continue
    if (!symbol || text(legs[0]?.instrument?.symbol).toUpperCase() !== symbol) continue
    const key = ref(order)
    // Ambiguous duplicate broker rows cannot establish a setup fill.
    byRef.set(key, byRef.has(key) ? null : order)
  }
  let anyFill = false
  let complete = true
  const quantities = new Map()
  for (const entry of entries) {
    const order = validRef(entry) ? byRef.get(ref(entry)) : null
    if (!order) { complete = false; continue }
    const quantity = Number(order.quantity)
    const filled = Number(order.filledQuantity)
    const isFilled = text(order.status).toUpperCase() === 'FILLED'
      && !(Number(order.remainingQuantity) > 0)
      && (order.filledQuantity == null || (quantity > 0 && filled >= quantity))
    anyFill ||= isFilled || filled > 0
    complete &&= isFilled
    quantities.set(account(entry), (quantities.get(account(entry)) || 0) + (isFilled ? quantity : 0))
  }
  // A successful subset of a split or multi-account submission is not a filled setup.
  for (const sizing of project.account_sizing || []) {
    if (Number(sizing.shares) > 0 && !((quantities.get(account(sizing)) || 0) >= Number(sizing.shares))) complete = false
  }
  for (const acct of new Set(entries.map(account))) {
    const roles = new Set(entries.filter(row => account(row) === acct).map(row => text(row.role).toUpperCase()))
    if (roles.has('TARGET_ENTRY') !== roles.has('RUNNER_ENTRY')) complete = false
  }
  if ((project.schwab_order_ids || []).some(row => row?.role === 'ORPHAN_NEEDS_CLEANUP')) complete = false
  return anyFill ? (complete ? 'filled' : 'partially filled') : null
}
